import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import LegacyFilterToolbar, {
  LEGACY_FILTER_INPUT_CLASS,
} from "components/Common/LegacyFilterToolbar";
import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Row,
} from "reactstrap";
import { toast } from "react-toastify";

import CommonTable from "components/Common/TableContainer";
import Loaders from "components/Common/Loaders";
import { AsyncClientSelect, StaticFilterSelect } from "components/Common/AsyncSearchSelects";
import {
    applyClientAsyncFilterOptionToFormState,
    buildClientAsyncFilterInitialOption,
    getClientAsyncFilterLabelFieldKey,
} from "helpers/metadataFilterHelpers";
import { submitSegmentCompletionReportFilterData } from "model/SegmentCompletionReportModel";

const TABLE_COLUMNS = [
    { Header: "SN", accessor: "sn" },
    { Header: "Client", accessor: "client" },
    {
        Header: "Average Application Complition Days",
        accessor: "averageApplicationComplitionDays",
    },
    { Header: "Average Eligible Marking Days", accessor: "averageEligibleMarkingDays" },
    { Header: "Average Updation Marked Days", accessor: "averageUpdationMarkedDays" },
];

const MONTH_OPTIONS = [
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
];

const YEAR_OPTIONS = Array.from({ length: 26 }, (_, index) => {
    const year = 2001 + index;
    return { value: String(year), label: String(year) };
});

const getDefaultMonth = () => String(new Date().getMonth() + 1);
const getDefaultYear = () => String(new Date().getFullYear());

const toNumberOrZero = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const strOrNull = (value) => {
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim();
    return normalized === "" ? null : normalized;
};

const mapRowsFromApi = (rows = []) =>
    (Array.isArray(rows) ? rows : []).map((item = {}, index) => ({
        ...item,
        sn: item?.sn ?? item?.serialNumber ?? item?.sN ?? index + 1,
        client: item?.client ?? item?.clientName ?? "",
        averageApplicationComplitionDays:
            item?.averageApplicationComplitionDays ??
            item?.average_application_complition_days ??
            item?.avgApplicationCompletionDays ??
            "",
        averageEligibleMarkingDays:
            item?.averageEligibleMarkingDays ??
            item?.average_eligible_marking_days ??
            item?.avgEligibleMarkingDays ??
            "",
        averageUpdationMarkedDays:
            item?.averageUpdationMarkedDays ??
            item?.average_updation_marked_days ??
            item?.avgUpdationMarkedDays ??
            "",
    }));

const SegmentCompletion = () => {
    const [filterForm, setFilterForm] = useState({
        month: getDefaultMonth(),
        year: getDefaultYear(),
        clientId: "",
        [getClientAsyncFilterLabelFieldKey()]: "",
    });
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);

    const filteredData = useMemo(() => reportData, [reportData]);

    const buildFilterPayload = () => ({
        month: toNumberOrZero(filterForm?.month),
        year: toNumberOrZero(filterForm?.year),
        clientId: strOrNull(filterForm?.clientId),
    });

    const getSegmentCompletionReportList = async () => {
        setLoading(true);
        try {
            const response = await submitSegmentCompletionReportFilterData(buildFilterPayload());
            const responseData = response?.data ?? response ?? {};
            const nestedData =
                responseData?.data && typeof responseData.data === "object"
                    ? responseData.data
                    : responseData;
            const records = Array.isArray(nestedData?.rows)
                ? nestedData.rows
                : Array.isArray(responseData?.rows)
                    ? responseData.rows
                    : Array.isArray(nestedData?.data)
                        ? nestedData.data
                        : Array.isArray(responseData?.data)
                            ? responseData.data
                            : Array.isArray(nestedData?.records)
                                ? nestedData.records
                                : Array.isArray(responseData?.records)
                                    ? responseData.records
                                    : Array.isArray(responseData)
                                        ? responseData
                                        : [];

            if (response == null || response?.success === false) {
                setReportData([]);
                if (response?.message) {
                    toast.error(response.message);
                }
                return;
            }

            setReportData(mapRowsFromApi(records));
        } catch (error) {
            toast.error(error?.message || "Unable to load segment completion report");
            setReportData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleMonthChange = (option) => {
        setFilterForm((previous) => ({
            ...previous,
            month: option?.value || "",
        }));
    };

    const handleYearChange = (option) => {
        setFilterForm((previous) => ({
            ...previous,
            year: option?.value || "",
        }));
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Card>
                        <CardBody>
                            <h5 className="fw-bold mb-3">Segment Completion Report</h5>
                            <hr />

                            <LegacyFilterToolbar className="w-100">
                                <Row className="metadata-filter-toolbar__row g-2 g-md-3 align-items-end">
                                    <Col xs="12" md="6" lg={3}>
                                        <span className="manage-user-filter-label">Month</span>
                                        <StaticFilterSelect
                                            options={MONTH_OPTIONS}
                                            placeholder="Select Month"
                                            value={filterForm?.month || ""}
                                            onChange={handleMonthChange}
                                            isClearable={false}
                                        />
                                    </Col>
                                    <Col xs="12" md="6" lg={3}>
                                        <span className="manage-user-filter-label">Year</span>
                                        <StaticFilterSelect
                                            options={YEAR_OPTIONS}
                                            placeholder="Select Year"
                                            value={filterForm?.year || ""}
                                            onChange={handleYearChange}
                                            isClearable={false}
                                        />
                                    </Col>
                                    <Col xs="12" md="6" lg={3}>
                                        <span className="manage-user-filter-label">Client</span>
                                        <AsyncClientSelect
                                            key={`segment-completion-client-${filterForm?.clientId || ""}`}
                                            pageName="ReportFiltered"
                                            value={filterForm?.clientId || ""}
                                            initialOption={buildClientAsyncFilterInitialOption(
                                                filterForm?.clientId,
                                                filterForm[getClientAsyncFilterLabelFieldKey()]
                                            )}
                                            onChange={(option) =>
                                                setFilterForm((previous) =>
                                                    applyClientAsyncFilterOptionToFormState(
                                                        previous,
                                                        option
                                                    )
                                                )
                                            }
                                            placeholder="Select Client"
                                        />
                                    </Col>
                                </Row>
                                <Row className="metadata-filter-toolbar__row g-2 g-md-3 align-items-end justify-content-end mt-1">
                                    <Col xs="12" md={{ size: 2, offset: 10 }} className="text-md-end">
                                        <Button
                                            color="primary"
                                            className="w-100 manage-user-search-btn"
                                            type="button"
                                            onClick={getSegmentCompletionReportList}
                                        >
                                            <i className="fa fa-search me-1" aria-hidden="true" />
                                            Search
                                        </Button>
                                    </Col>
                                </Row>
                            </LegacyFilterToolbar>

                            {loading ? (
                                <Loaders />
                            ) : (
                                <CommonTable
                                    columns={TABLE_COLUMNS}
                                    data={filteredData}
                                    isActionRequired={false}
                                />
                            )}
                        </CardBody>
                    </Card>
                </Container>
            </div>
        </React.Fragment>
    );
};

SegmentCompletion.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(SegmentCompletion);
